<script lang="ts" setup>
    import BaseButton from '@/components/BaseButton.vue';
    import TextEditForm from '@/components/TextEditForm.vue';
    import { useTemplateStore } from '@/store/template.store';
    import { getEmptyTextboxField } from '@/utils/emptyTemplate';
    import UploadImageModal from '@/components/UploadImageModal.vue';
    import IconPhoto from '@/components/icons/IconPhoto.vue';
    import IconGrid from '@/components/icons/IconGrid.vue';
    import IconGroup from '@/components/icons/IconGroup.vue';
    import IconVector from '@/components/icons/IconVector.vue';
    import IconMeasurement from '@/components/icons/IconMeasurement.vue';
    import { useGraduation } from '@/composables/useGraduation';
    import { useGrid } from '@/composables/useGrid';
    import { useConfigStore } from '@/store/config.store';
    import IconCisors from '@/components/icons/IconCisors.vue';
    import BaseIcon from '@/components/BaseIcon.vue';
    import { debugCartPdf } from '@/utils/addToCart';
    import { storeToRefs } from 'pinia';
    import { computed, ref } from 'vue';
    import type { ITextField } from '@/typescript/interfaces/ITextField';
    import type { TUploadImgType } from '@/typescript/interfaces/TUploadImgType';
    const templateStore = useTemplateStore();

    const { selectedField } = storeToRefs(useTemplateStore());
    const { hasGraduation, toggleGraduation } = useGraduation();
    const { displayGrid, toggleGrid } = useGrid();
    const { currentSide, isAdmin, isBATModalVisible, isCup, isProductEdition } = storeToRefs(useConfigStore());
    const isTextFormVisible = computed(() => selectedField.value && 'defaultText' in selectedField.value);

    const isUploadModalVisible = ref(false);
    const uploadImgType = ref<TUploadImgType | undefined>();

    function addImageField() {
        isUploadModalVisible.value = true;
    }
    function displayPreviewBATModal() {
        isBATModalVisible.value = true;
    }
    function addTextField() {
        templateStore.addField({ ...getEmptyTextboxField(), side: currentSide.value });
    }
    function addCutImageField() {
        uploadImgType.value = 'cutPath';
        addImageField();
    }
    function addGridImageField() {
        uploadImgType.value = 'helpGrid';
        addImageField();
    }
</script>
<template>
    <div class="border-b border-t border-secondary">
        <div class="flex h-16 w-full flex-row items-center justify-between gap-2 bg-white px-2">
            <div
                v-if="isProductEdition"
                class="flex flex-none gap-2 md:gap-2"
            >
                <BaseButton
                    variant="outlined"
                    class="h-8 gap-0 px-2 py-3 md:gap-1 md:px-3.5 md:text-sm"
                    @click="addGridImageField"
                >
                    <IconGrid class="mr-0.5" />
                    {{ $t("Ajout de grille d'aide") }}
                </BaseButton>
                <BaseButton
                    variant="outlined"
                    :disabled="currentSide === 'verso'"
                    class="h-8 gap-0 px-2 py-3 md:gap-1 md:px-3.5 md:text-sm"
                    @click="addCutImageField"
                >
                    <IconCisors class="mr-0.5" />
                    {{ $t('Découpe non visible BAT') }}
                </BaseButton>
            </div>
            <div
                v-else
                class="flex flex-none gap-2 md:gap-2"
            >
                <BaseButton
                    variant="outlined"
                    class="h-8 gap-0 px-2 py-3 md:gap-1 md:px-3.5 md:text-sm"
                    @click="addImageField"
                >
                    <IconPhoto class="mr-0.5" />
                    {{ $t('image') }}
                </BaseButton>

                <BaseButton
                    variant="outlined"
                    class="h-8 gap-0 whitespace-nowrap px-2 py-3 md:gap-1 md:px-3.5 md:text-sm"
                    @click="addTextField"
                >
                    <IconVector class="mr-0.5" />
                    {{ $t('text') }}
                </BaseButton>
                <BaseButton
                    v-if="isCup"
                    :variant="hasGraduation ? 'filled' : 'outlined'"
                    class="h-8 gap-1 whitespace-nowrap px-2 py-3 md:gap-3 md:px-3.5 md:text-sm"
                    @click="toggleGraduation"
                >
                    <IconMeasurement class="mr-0.5" />
                    {{ $t('graduation') }}
                </BaseButton>
            </div>

            <div class="thin-scrollbar flex h-full min-w-0 grow justify-center overflow-x-auto overflow-y-hidden">
                <TextEditForm
                    v-if="isTextFormVisible"
                    :field="selectedField as ITextField"
                />
            </div>

            <div
                v-if="!isProductEdition"
                class="flex flex-none gap-2 md:gap-2"
            >
                <BaseButton
                    :variant="displayGrid ? 'filled' : 'outlined'"
                    class="h-8 gap-1 whitespace-nowrap px-2 py-3 md:gap-3 md:px-3.5 md:text-sm"
                    @click="toggleGrid"
                >
                    <IconGrid class="mr-0.5" />
                    {{ $t('help_grid') }}
                </BaseButton>
                <BaseButton
                    variant="outlined"
                    class="h-8 gap-1 whitespace-nowrap px-2 py-3 md:gap-3 md:px-3.5 md:text-sm"
                    @click="displayPreviewBATModal"
                >
                    <IconGroup class="mr-0.5" />
                    {{ $t('bat_preview') }}
                </BaseButton>
                <BaseButton
                    v-if="isAdmin && !isProductEdition"
                    class="h-8 gap-1 whitespace-nowrap px-1 py-3 md:gap-3 md:px-3.5 md:text-sm"
                    @click="debugCartPdf"
                >
                    <BaseIcon name="ArrowDownTrayIcon" />
                </BaseButton>
            </div>
        </div>
    </div>
    <transition
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
        class="duration-300"
    >
        <UploadImageModal
            v-if="isUploadModalVisible"
            :current-side
            :upload-img-type
            @close="
                () => {
                    isUploadModalVisible = false;
                    uploadImgType = undefined;
                }
            "
        />
    </transition>
</template>

<style>
    .thin-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #a0a0a0;
    }

    .thin-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: #cdcdcd transparent;
    }
</style>
